import React, { useState } from 'react';
import { Upload, Button, Progress, message } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';

interface VideoUploadProps {
  value?: File | string;
  onChange?: (file: File | null) => void;
  disabled?: boolean;
  progress?: number;
}

const VideoUpload: React.FC<VideoUploadProps> = ({
  value,
  onChange,
  disabled = false,
  progress = 0,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleUpload: UploadProps['customRequest'] = ({ file, onSuccess }) => {
    // Don't upload immediately, just save file for later upload
    if (onChange) {
      onChange(file as File);
    }
    if (onSuccess) {
      onSuccess('ok');
    }
  };

  const handleRemove = () => {
    setFileList([]);
    if (onChange) {
      onChange(null);
    }
  };

  const handleBeforeUpload = (file: File) => {
    const isVideo = file.type.startsWith('video/');
    if (!isVideo) {
      message.error('Only video files can be uploaded!');
      return false;
    }

    const isLt500M = file.size / 1024 / 1024 < 500;
    if (!isLt500M) {
      message.error('Video must be less than 500MB!');
      return false;
    }

    return true;
  };

  const handleChange: UploadProps['onChange'] = (info) => {
    setFileList(info.fileList);
  };

  return (
    <div>
      <Upload
        customRequest={handleUpload}
        beforeUpload={handleBeforeUpload}
        onChange={handleChange}
        fileList={fileList}
        maxCount={1}
        disabled={disabled}
        accept="video/*"
      >
        <Button 
          icon={<UploadOutlined />} 
          disabled={disabled || fileList.length >= 1}
        >
          Select video
        </Button>
      </Upload>
      
      {progress > 0 && progress < 100 && (
        <div style={{ marginTop: 8 }}>
          <Progress percent={progress} size="small" />
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            Uploading video...
          </div>
        </div>
      )}
      
      {typeof value === 'string' && value && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: '#666' }}>
            Current video: 
            <a href={value} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4 }}>
              View video
            </a>
            <Button 
              type="text" 
              size="small" 
              icon={<DeleteOutlined />} 
              onClick={handleRemove}
              style={{ marginLeft: 8 }}
            >
              Delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoUpload;